/*!
 * @jscrypto/hashes v0.9.1
 * Copyright 2026 Chen, Yi-Cyuan
 * Released under the MIT license
 */
import { md5 as md5$1 } from 'js-md5';
import { sha1 as sha1$1 } from 'js-sha1';
import { sha224 as sha224$1, sha256 as sha256$1 } from 'js-sha256';
import * as sha3Module from 'js-sha3';
import { sha384 as sha384$1, sha512 as sha512$1 } from 'js-sha512';

function createHash(name, blockSize, digestSize, hash) {
    return {
        kind: 'hash',
        name,
        blockSize,
        digestSize,
        hash,
    };
}

const md5 = createHash('MD5', 64, 16, (input) => new Uint8Array(md5$1.arrayBuffer(input)));
const md5Preset = {
    kind: 'preset',
    name: 'md5',
    components() {
        return [md5];
    },
};

const MESSAGE_LEFT = [
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15,
    7, 4, 13, 1, 10, 6, 15, 3, 12, 0, 9, 5, 2, 14, 11, 8,
    3, 10, 14, 4, 9, 15, 8, 1, 2, 7, 0, 6, 13, 11, 5, 12,
    1, 9, 11, 10, 0, 8, 12, 4, 13, 3, 7, 15, 14, 5, 6, 2,
    4, 0, 5, 9, 7, 12, 2, 10, 14, 1, 3, 8, 11, 6, 15, 13,
];
const MESSAGE_RIGHT = [
    5, 14, 7, 0, 9, 2, 11, 4, 13, 6, 15, 8, 1, 10, 3, 12,
    6, 11, 3, 7, 0, 13, 5, 10, 14, 15, 8, 12, 4, 9, 1, 2,
    15, 5, 1, 3, 7, 14, 6, 9, 11, 8, 12, 2, 10, 0, 4, 13,
    8, 6, 4, 1, 3, 11, 15, 0, 5, 12, 2, 13, 9, 7, 10, 14,
    12, 15, 10, 4, 1, 5, 8, 7, 6, 2, 13, 14, 0, 3, 9, 11,
];
const ROTATE_LEFT = [
    11, 14, 15, 12, 5, 8, 7, 9, 11, 13, 14, 15, 6, 7, 9, 8,
    7, 6, 8, 13, 11, 9, 7, 15, 7, 12, 15, 9, 11, 7, 13, 12,
    11, 13, 6, 7, 14, 9, 13, 15, 14, 8, 13, 6, 5, 12, 7, 5,
    11, 12, 14, 15, 14, 15, 9, 8, 9, 14, 5, 6, 8, 6, 5, 12,
    9, 15, 5, 11, 6, 8, 13, 12, 5, 12, 13, 14, 11, 8, 5, 6,
];
const ROTATE_RIGHT = [
    8, 9, 9, 11, 13, 15, 15, 5, 7, 7, 8, 11, 14, 14, 12, 6,
    9, 13, 15, 7, 12, 8, 9, 11, 7, 7, 12, 7, 6, 15, 13, 11,
    9, 7, 15, 11, 8, 6, 6, 14, 12, 13, 5, 14, 13, 13, 7, 5,
    15, 5, 8, 11, 14, 14, 6, 14, 6, 9, 12, 9, 12, 5, 15, 8,
    8, 5, 12, 9, 12, 5, 14, 6, 8, 13, 6, 5, 15, 13, 11, 11,
];
const K_LEFT = [0x00000000, 0x5a827999, 0x6ed9eba1, 0x8f1bbcdc, 0xa953fd4e];
const K_RIGHT = [0x50a28be6, 0x5c4dd124, 0x6d703ef3, 0x7a6d76e9, 0x00000000];
const ripemd160 = createHash('RIPEMD160', 64, 20, ripemd160Digest);
const ripemd160Preset = {
    kind: 'preset',
    name: 'ripemd160',
    components() {
        return [ripemd160];
    },
};
function ripemd160Digest(input) {
    const padded = pad(input);
    const words = new Array(16);
    let h0 = 0x67452301;
    let h1 = 0xefcdab89;
    let h2 = 0x98badcfe;
    let h3 = 0x10325476;
    let h4 = 0xc3d2e1f0;
    for (let offset = 0; offset < padded.length; offset += 64) {
        for (let i = 0; i < 16; i++) {
            const wordOffset = offset + i * 4;
            words[i] = (padded[wordOffset] |
                (padded[wordOffset + 1] << 8) |
                (padded[wordOffset + 2] << 16) |
                (padded[wordOffset + 3] << 24)) >>> 0;
        }
        let al = h0;
        let bl = h1;
        let cl = h2;
        let dl = h3;
        let el = h4;
        let ar = h0;
        let br = h1;
        let cr = h2;
        let dr = h3;
        let er = h4;
        for (let i = 0; i < 80; i++) {
            const group = Math.floor(i / 16);
            let next = (rotateLeft((al + f(i, bl, cl, dl) + words[MESSAGE_LEFT[i]] + K_LEFT[group]) | 0, ROTATE_LEFT[i]) + el) | 0;
            al = el;
            el = dl;
            dl = rotateLeft(cl, 10);
            cl = bl;
            bl = next;
            next = (rotateLeft((ar + f(79 - i, br, cr, dr) + words[MESSAGE_RIGHT[i]] + K_RIGHT[group]) | 0, ROTATE_RIGHT[i]) + er) | 0;
            ar = er;
            er = dr;
            dr = rotateLeft(cr, 10);
            cr = br;
            br = next;
        }
        const nextH0 = (h1 + cl + dr) | 0;
        h1 = (h2 + dl + er) | 0;
        h2 = (h3 + el + ar) | 0;
        h3 = (h4 + al + br) | 0;
        h4 = (h0 + bl + cr) | 0;
        h0 = nextH0;
    }
    const output = new Uint8Array(20);
    writeWord(output, 0, h0);
    writeWord(output, 4, h1);
    writeWord(output, 8, h2);
    writeWord(output, 12, h3);
    writeWord(output, 16, h4);
    return output;
}
function pad(input) {
    const paddedLength = (((input.length + 8) >>> 6) + 1) << 6;
    const padded = new Uint8Array(paddedLength);
    padded.set(input);
    padded[input.length] = 0x80;
    const bitLengthLow = (input.length << 3) >>> 0;
    const bitLengthHigh = Math.floor(input.length / 0x20000000) >>> 0;
    writeWord(padded, paddedLength - 8, bitLengthLow);
    writeWord(padded, paddedLength - 4, bitLengthHigh);
    return padded;
}
function f(round, x, y, z) {
    if (round < 16) {
        return x ^ y ^ z;
    }
    if (round < 32) {
        return (x & y) | (~x & z);
    }
    if (round < 48) {
        return (x | ~y) ^ z;
    }
    if (round < 64) {
        return (x & z) | (y & ~z);
    }
    return x ^ (y | ~z);
}
function rotateLeft(word, bits) {
    return (word << bits) | (word >>> (32 - bits));
}
function writeWord(output, offset, word) {
    output[offset] = word & 0xff;
    output[offset + 1] = (word >>> 8) & 0xff;
    output[offset + 2] = (word >>> 16) & 0xff;
    output[offset + 3] = (word >>> 24) & 0xff;
}

const sha1 = createHash('SHA1', 64, 20, (input) => new Uint8Array(sha1$1.arrayBuffer(input)));
const sha1Preset = {
    kind: 'preset',
    name: 'sha1',
    components() {
        return [sha1];
    },
};

const sha224 = createHash('SHA224', 64, 28, (input) => new Uint8Array(sha224$1.arrayBuffer(input)));
const sha224Preset = {
    kind: 'preset',
    name: 'sha224',
    components() {
        return [sha224];
    },
};

const sha256 = createHash('SHA256', 64, 32, (input) => new Uint8Array(sha256$1.arrayBuffer(input)));
const sha256Preset = {
    kind: 'preset',
    name: 'sha256',
    components() {
        return [sha256];
    },
};

const keccak512Digest = sha3Module.default.keccak512;
const hashKeccak512 = (input) => new Uint8Array(keccak512Digest.arrayBuffer(input));
const keccak512 = createHash('KECCAK512', 72, 64, hashKeccak512);
const keccak512Preset = {
    kind: 'preset',
    name: 'keccak512',
    components() {
        return [keccak512];
    },
};

const sha384 = createHash('SHA384', 128, 48, (input) => new Uint8Array(sha384$1.arrayBuffer(input)));
const sha384Preset = {
    kind: 'preset',
    name: 'sha384',
    components() {
        return [sha384];
    },
};

const sha512 = createHash('SHA512', 128, 64, (input) => new Uint8Array(sha512$1.arrayBuffer(input)));
const sha512Preset = {
    kind: 'preset',
    name: 'sha512',
    components() {
        return [sha512];
    },
};

const hashesPreset = {
    kind: 'preset',
    name: 'hashes',
    components() {
        return [
            md5Preset,
            sha1Preset,
            sha224Preset,
            sha256Preset,
            sha384Preset,
            sha512Preset,
            keccak512Preset,
            ripemd160Preset,
        ];
    },
};
const classicHashesPreset = {
    kind: 'preset',
    name: 'classic-hashes',
    components: hashesPreset.components,
};

export { classicHashesPreset, createHash, hashesPreset, keccak512, md5, ripemd160, sha1, sha224, sha256, sha384, sha512 };
//# sourceMappingURL=index.mjs.map
